import { Activity, AlertCircle, Anchor, AppWindow, Archive, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUpDown, ArrowUpRight, Award, BadgeDollarSign, Bell, BellDot, Bot, Briefcase, Building, Building as Apartment, Building2, Cake, Calendar, Camera, ChartColumn, ChartPie, Check, CheckSquare, ChevronDown, ChevronUp, Circle, CircleCheck, CircleUser, ClipboardCheck, ClipboardPenLine, Clock, CloudUpload, Columns3, Construction, Contact, CreditCard, DollarSign, ExternalLink, FileSignature, FileText, FileText as Description, Fish, Gauge, Gavel, Globe, Handshake, Heart, History, House, HousePlus, IdCard, Image, Inbox, Key, Languages, Layers, LayoutDashboard, Library, LayoutGrid, Lightbulb, List, ListChecks, LogOut, Mail, MapPin, Megaphone, MousePointerClick, Mic, MessageCircle, MessageSquare, Minus, MonitorCog, MoreVertical, Palette, Pencil, PencilOff, Phone, Play, Plus, Presentation, Receipt, RefreshCw, Save, Search, Settings, Settings2, Shield, ShieldAlert, ShieldCheck, ShieldHalf, ShieldUser, Sliders, SlidersHorizontal, SquareChevronRight, Star, SunMoon, Tag, Trash2, TrendingUp, Upload, User, UserGroup, Users, UserSearch, UsersRound, X, Zap, type LucideIcon, Blocks, BriefcaseBusiness, Cog, Grid2x2Check, LayoutList, Mails, PenLine, Banknote, TrendingUpDown, Map as MapIcon, Smartphone } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  "plus": Plus,
  "archive": Archive,
  "arrowleft": ArrowLeft,
  "arrowright": ArrowRight,
  "arrowleftright": ArrowLeftRight,
  "dollarsign": DollarSign,
  "award": Award,
  "building2": Building2,
  "briefcase": Briefcase,
  "cake": Cake,
  "camera": Camera,
  "check": Check,
  "checkcircle": CircleCheck,
  "clipboardcheck": ClipboardCheck,
  "listchecks": ListChecks,
  "circle": Circle,
  "x": X,
  "cloudupload": CloudUpload,
  "creditcard": CreditCard,
  "layoutdashboard": LayoutDashboard,
  "gauge": Gauge,
  "presentation": Presentation,
  // The Agent Portal menu item (Andrew, 2026-10-21).
  "usergroup": UserGroup,
  "trash2": Trash2,
  "filetext": FileText,
  "building": Building,
  "edit": Pencil,
  "mail": Mail,
  "alertcircle": AlertCircle,
  "calendar": Calendar,
  "chevronup": ChevronUp,
  "chevrondown": ChevronDown,
  "expandless": ChevronUp,
  "expandmore": ChevronDown,
  "heart": Heart,
  "gavel": Gavel,
  "shieldalert": ShieldAlert,
  "grid": LayoutGrid,
  "users": Users,
  "history": History,
  "home": House,
  "image": Image,
  "languages": Languages,
  "lightbulb": Lightbulb,
  "mappin": MapPin,
  "map": MapIcon,
  "keyround": Key,
  "logout": LogOut,
  "morevertical": MoreVertical,
  "bell": Bell,
  "externallink": ExternalLink,
  "clock": Clock,
  "user": User,
  "usersearch": UserSearch,
  "phone": Phone,
  // Showroom on the buyer's own phone, on the dashboard's activity widget.
  "smartphone": Smartphone,
  "play": Play,
  "refreshcw": RefreshCw,
  "minus": Minus,
  "palette": Palette,
  "save": Save,
  "search": Search,
  "settings": Settings,
  "shield": Shield,
  "bot": Bot,
  "star": Star,
  "arrowupdown": ArrowUpDown,
  "tag": Tag,
  "upload": Upload,
  "checkcircle2": CircleCheck,
  "list": List,
  "checksquare": CheckSquare,
  "layers": Layers,
  "barchart": ChartColumn,
  "sliders": Sliders,
  "shieldcheck": ShieldCheck,
  "appwindow": AppWindow,
  "messagecircle": MessageCircle,
  "slidershorizontal": SlidersHorizontal,
  "anchor": Anchor,
  "chartbar": ChartColumn,
  "circlecheck": CircleCheck,
  "mails": Mails,
  // Website Analytics (Andrew, 2026-11-01).
  "mousepointerclick": MousePointerClick,
  "layoutlist": LayoutList,
  "penline": PenLine,
  "banknote": Banknote,
  "filesignature": FileSignature,
  "chartcolumn": ChartColumn,
  "badgedollarsign": BadgeDollarSign,
  "columns3": Columns3,
  "briefcasebusiness": BriefcaseBusiness,
  "filecheck": FileSignature,
  "houseplus": HousePlus,
  "idcard": IdCard,
  "usersround": UsersRound,
  "botmessagesquare": Bot,
  "mic": Mic,
  "messagesquare": MessageSquare,
  "belldot": BellDot,
  "sunmoon": SunMoon,
  "fish": Fish,
  "contact": Contact,
  "circleuser": CircleUser,
  "handshake": Handshake,
  "shieldhalf": ShieldHalf,
  "settings2": Settings2,
  "email": Mail,
  "event": Calendar,
  "followup": ArrowUpRight,
  "taskalt": CheckSquare,
  "person": User,
  "business": Briefcase,
  "construction": Construction,
  "campaign": Megaphone,
  "trendingup": TrendingUp,
  "editoff": PencilOff,
  "sidenavigation": SquareChevronRight,
  "close": X,
  "bolt": Zap,
  "monitorcog": MonitorCog,
  "trendingupdown": TrendingUpDown,
  "globe": Globe,
  "megaphone": Megaphone,
  "inbox": Inbox,
  "blocks": Blocks,
  "grid2x2check": Grid2x2Check,
  // These fell through to the Circle fallback: the Home tab drew a ring, and
  // Project Settings had no glyph at all. A navigation icon must be the glyph
  // its menu item names — "Integrations" was drawing the Dashboard icon.
  "house": House,
  "cog": Cog,
  "receipt": Receipt,
  "chartpie": ChartPie,
  "apartment": Apartment,
  "description": Description,
  // Andrew's icon table, 2026-10-28: Offers (the same as a worksheet), Usage,
  // Roles and the Document Library each draw their own idea's glyph.
  "clipboardpenline": ClipboardPenLine,
  "activity": Activity,
  "shielduser": ShieldUser,
  "library": Library,
};

interface DynamicIconProps {
  name: string;
  className?: string;
}

/**
 * A named icon, for the places where the NAME is data — menu configuration,
 * project settings, widget registries — rather than a component reference in
 * source. `iconMap` above is deliberately a fixed list: an unknown name warns
 * and falls back rather than silently rendering nothing.
 *
 * NO WEIGHT PROP. Phosphor shipped six stroke weights per icon and this
 * component exposed one, which the sidebar briefly used to fill the selected
 * item's glyph. That was wrong on its own terms — a menu of ten outline icons
 * with one solid one reads as two different icon sets rather than as one
 * selected row, because a fill changes the glyph's whole silhouette instead of
 * just emphasising it — and lucide has no equivalent anyway: it draws with
 * `strokeWidth`, and nothing in this app varies it. The selected row is carried
 * by the pill background and the primary-coloured label.
 */
export function DynamicIcon({ name, className = "h-5 w-5" }: DynamicIconProps) {
  const IconComponent = iconMap[name.toLowerCase().replace(/[_\s-]/g, "")];

  if (!IconComponent) {
    console.warn(`Icon not found: ${name}`);
    return <Circle className={className} />;
  }

  return <IconComponent className={className} />;
}
